/**
 * What shape a poll takes.
 *
 * - `choice`  — pick one of 2–5 options.
 * - `yes-no`  — a yes or no; the options are always ["Yes", "No"].
 * - `rating`  — a 1–5 scale; the options are its five level labels.
 * - `ranking` — order 2–5 items by how much they matter.
 *
 * Declared here, not beside the entities, because this file is loaded as
 * source by apps/web — and importing the entity classes would pull them
 * through the web app's stricter compiler settings too.
 */
export type ResearchPollFormat = 'choice' | 'rating' | 'ranking' | 'yes-no';

/**
 * Turning a typed question into a poll's parts — in code, never by a model.
 *
 * Shared because both halves need it: the composer reads options out of the
 * question as it is typed, and the API does the same for a question that was
 * sent plain but reads like a poll. Runtime values, so exported twice — from
 * `entry.ts` for apps/api and as the `./research-poll` source subpath for
 * apps/web, the arrangement `target-url.ts` explains.
 * The model only says *what kind* of question it is; which options it names
 * is read straight out of the words, and shown to the researcher to fix
 * before anyone votes.
 */

export const MAX_OPTIONS = 5;

function tidy(option: string): string {
  return option.replace(/^[\s"'“‘(]+|[\s"'”’)?.!]+$/g, '').trim();
}

/** Words after which an "A or B" choice starts — "would you *rather* A or
 *  B", "*click* A or B". Without one, A is the last few words before "or". */
const LEAD_IN =
  /\b(?:rather|prefer|choose|pick|click|tap|try|want|use|see|between|like|is it|are they)\s+/gi;

function unique(options: string[]): string[] {
  const seen = new Set<string>();
  return options.filter((option) => {
    const key = option.toLowerCase();
    if (!option || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * The options a question names, if it names any:
 *
 * - one per line (a pasted list),
 * - a list after a colon — "Which first: Try free, Watch demo or Pilot?",
 * - quoted phrases — Would you click "Try free" or "Book a call"?,
 * - "A or B" at the end of the question.
 *
 * Returns what it finds, which may be nothing; the card then opens with
 * empty options to fill.
 */
export function extractOptions(text: string): string[] {
  const lines = text
    .split('\n')
    .map((line) => line.replace(/^\s*([-*•]|\d+[.)])\s*/, '').trim())
    .filter(Boolean);
  if (lines.length >= 3) {
    return unique(lines.slice(1).map(tidy)).slice(0, MAX_OPTIONS);
  }

  const flat = text.replace(/\s+/g, ' ').trim();

  const quoted = [...flat.matchAll(/["“]([^"”]{1,80})["”]/g)].map((m) =>
    tidy(m[1] ?? ''),
  );
  if (quoted.length >= 2) return unique(quoted).slice(0, MAX_OPTIONS);

  const afterColon = /:\s*(.+)$/.exec(flat)?.[1];
  const tail = afterColon ?? orPhrase(flat);
  if (!tail) return [];

  const parts = tail
    .replace(/\?+$/, '')
    .split(/\s*,\s*(?:or\s+|and\s+)?|\s+or\s+|\s+and\s+|\s*\/\s*/i)
    .map(tidy)
    .filter((part) => part.length > 0 && part.length <= 80);
  return parts.length >= 2 ? unique(parts).slice(0, MAX_OPTIONS) : [];
}

/** The "A or B" (or "A, B or C") phrase at the end of a question, starting
 *  after its last lead-in word. */
function orPhrase(flat: string): string | null {
  const sentence = flat.replace(/\?+$/, '');
  if (!/\s+or\s+/i.test(sentence)) return null;

  let start = -1;
  for (const match of sentence.matchAll(LEAD_IN)) {
    start = match.index + match[0].length;
  }
  if (start >= 0) return sentence.slice(start);

  // No lead-in: take a short run of words either side of the first "or".
  const [before = '', ...after] = sentence.split(/\s+or\s+/i);
  const words = before.split(/\s+/);
  return `${words.slice(-3).join(' ')} or ${after.join(' or ')}`;
}

const LEVEL_WORDS = [
  'Not at all',
  'Slightly',
  'Somewhat',
  'Very',
  'Completely',
] as const;

/**
 * Five labels, low to high, for a rating — worded from the question when it
 * says what is being rated ("How *confident*…" → "Not at all confident" …
 * "Completely confident"), a quality scale for "rate the …", and an agree
 * scale for a statement.
 */
export function ratingLevels(question: string): string[] {
  const word = /\bhow\s+(\w+)/i.exec(question)?.[1]?.toLowerCase();
  const scalable =
    word &&
    ![
      'do',
      'does',
      'did',
      'would',
      'will',
      'can',
      'could',
      'should',
      'is',
      'are',
      'much',
      'many',
      'often',
    ].includes(word);
  if (scalable) return LEVEL_WORDS.map((level) => `${level} ${word}`);
  // "Rate the audit feature" asks how good something is; "Rate this: the
  // page is trustworthy" asks how far they agree with a statement.
  const statement = /:\s*\S/.test(question);
  if (!statement && /\b(rate|rating|score|grade|out of)\b/i.test(question)) {
    return ['Very poor', 'Poor', 'Okay', 'Good', 'Excellent'];
  }
  return [
    'Strongly disagree',
    'Disagree',
    'Neutral',
    'Agree',
    'Strongly agree',
  ];
}

/** The options a format starts with, from the question as typed. */
export function initialOptions(
  format: ResearchPollFormat,
  question: string,
): string[] {
  switch (format) {
    case 'yes-no':
      return ['Yes', 'No'];
    case 'rating':
      return ratingLevels(question);
    case 'choice':
    case 'ranking': {
      const found = extractOptions(question);
      return found.length >= 2 ? found : ['', ''];
    }
  }
}

/** Why a draft cannot be sent yet, or null when it can. */
export function draftProblem(
  format: ResearchPollFormat,
  options: string[],
): string | null {
  if (format === 'yes-no' || format === 'rating') return null;
  const filled = options.map((option) => option.trim()).filter(Boolean);
  if (filled.length < 2) return 'Add at least two options';
  if (new Set(filled.map((o) => o.toLowerCase())).size !== filled.length) {
    return 'Two options are the same';
  }
  return null;
}
